// Pages whose address carries a secret: the password-reset and email-confirmation
// links (#1079). New links put the token after `#`, old ones in the path; either
// way the address must never reach Google Analytics or Sentry. Anything that
// reports an address on its own goes through here first.
//
// Mirrors lib/secret_paths.rb (the server's list). No imports, so it can be
// used from any entry point.

// `/reset-password/<anything>`, `/reset-password#<anything>` and the same for
// `/verify-email`, up to whitespace or a quote, so it also works on a full
// address and in the middle of a sentence. A query string on these pages goes
// too; every other address is left alone.
const SECRET_PATH = /\/(reset-password|verify-email)(?:[/#?][^\s"'<>]*)?/g;

export function redactSecretPaths(text: string): string {
  return text.replace(SECRET_PATH, "/$1");
}

// Puts every string inside `value` through redactSecretPaths, changing objects
// and arrays in place (Sentry expects the same event object back).
export function redactSecretPathsDeep<T>(value: T, seen = new WeakSet<object>()): T {
  if (typeof value === "string") return redactSecretPaths(value) as T;
  if (value === null || typeof value !== "object") return value;
  if (seen.has(value)) return value;
  seen.add(value);
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const child = record[key];
    if (typeof child === "string") {
      record[key] = redactSecretPaths(child);
    } else if (child !== null && typeof child === "object") {
      redactSecretPathsDeep(child, seen);
    }
  }
  return value;
}
